import { scmUnsupported, useScmConnections } from '@/api/hooks';
import { useFeature } from '@/lib/permissions';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { StatusPill, Tag } from '@/components/badges';
import { ProviderAccountsTable } from '@/features/integrations/ProviderAccountsTable';
import { TestResultChecks } from '@/features/integrations/TestResultChecks';
import { PROVIDED_BY_HANGAR } from '@/features/integrations/providerAccountModel';
import { useProviderAccountActions } from '@/features/integrations/useProviderAccountActions';
import { ScmConnectionsList } from '@/features/source-control/ScmConnectionsList';
import { useScmConnectionActions } from '@/features/source-control/useScmConnectionActions';
import { RowsSkeleton, type StepProps } from './StepShared';
import { StepCard } from './StepCard';
import { clientProvidersNote, providersNote, listView } from './stepNotesModel';
import { STEP_TITLE } from './steps';
import type { Setup } from './useSetup';

type AccountActions = ReturnType<typeof useProviderAccountActions>;
type ScmActions = ReturnType<typeof useScmConnectionActions>;

/** Client admins: Malible sets the build service up. Operators: where the Bitrise token comes from, or that Bitrise is not served yet. */
function ProvidersFootnote({ bitrise, operator }: { bitrise: boolean; operator: boolean }) {
  if (!operator) {
    return (
      <span className="inline-flex items-center gap-1.5" data-testid="gs-provided-by-malible">
        <Tag>Provided by Hangar</Tag>
        {PROVIDED_BY_HANGAR}
      </span>
    );
  }
  if (bitrise) return <span>Bitrise token: bitrise.io → your avatar → Account settings → Security → Personal access tokens.</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      Bitrise <Tag>Available soon</Tag>
    </span>
  );
}

interface FooterProps {
  actions: AccountActions;
  scm: ScmActions;
  sourceControl: boolean;
  bitrise: boolean;
  current: boolean;
}

/** Connect Bitbucket (when source control is served); operators also connect Bitrise and Expo accounts (SPEC decision 19). */
function ProvidersFooter({ actions, scm, sourceControl, bitrise, current }: FooterProps) {
  const bitriseBlocked = bitrise ? actions.tokenBlocked : 'Bitrise builds are available soon';
  const bitbucket = sourceControl ? (
    <GatedButton size="sm" variant={current && !actions.operator ? 'default' : 'outline'} data-testid="gs-connect-bitbucket" can={scm.can} blockedReason={scm.blocked} onClick={scm.connect}>
      <Icon name="add" size={16} />
      Connect Bitbucket
    </GatedButton>
  ) : null;
  if (!actions.operator) return bitbucket;
  return (
    <>
      {bitbucket}
      <GatedButton size="sm" variant="outline" data-testid="gs-connect-bitrise" can={actions.can} blockedReason={bitriseBlocked} onClick={() => actions.connect('bitrise')}>
        <Icon name="add" size={16} />
        Connect Bitrise account
      </GatedButton>
      <GatedButton size="sm" variant={current ? 'default' : 'outline'} data-testid="gs-connect-expo" can={actions.can} blockedReason={actions.tokenBlocked} onClick={() => actions.connect('eas')}>
        <Icon name="add" size={16} />
        Connect Expo account
      </GatedButton>
    </>
  );
}

/** The accounts table, then the last "Test connection" of each account in this visit: every check, and why one did not pass. */
function AccountsList({ setup, actions }: { setup: Setup; actions: AccountActions }) {
  const accounts = setup.providerAccounts;
  return (
    <>
      <ProviderAccountsTable accounts={accounts} actions={actions} bitrise={setup.bitrise} />
      {accounts
        .filter((a) => actions.results[a.id])
        .map((a) => (
          <TestResultChecks key={a.id} result={actions.results[a.id]!} accountName={a.name} />
        ))}
    </>
  );
}

/** A client's view of the build service (SPEC decision 23): ready or on its way, never the accounts behind it. */
function ClientBuildService({ setup }: { setup: Setup }) {
  const ready = setup.providerAccounts.some((a) => a.status !== 'disabled');
  return (
    <div data-testid="gs-build-service" className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-[13px]">
      <StatusPill status={ready ? 'succeeded' : 'queued'} label={ready ? 'Ready' : 'On its way'} still />
      <span className="min-w-0 text-muted-foreground">{ready ? "Hangar's build service is set up for your organization." : `${PROVIDED_BY_HANGAR} Ask them if it is not ready soon.`}</span>
    </div>
  );
}

function AccountsBody({ setup, actions }: { setup: Setup; actions: AccountActions }) {
  if (!actions.operator) return setup.accounts.isLoading && !setup.accounts.data ? <RowsSkeleton rows={1} /> : <ClientBuildService setup={setup} />;
  if (setup.accounts.isError && !setup.accounts.data) return <ErrorState compact error={setup.accounts.error} onRetry={() => void setup.accounts.refetch()} title="Couldn't load the build providers" />;
  if (setup.providerAccounts.length > 0) return <AccountsList setup={setup} actions={actions} />;
  return (
    <EmptyState
      compact
      testId="gs-accounts-empty"
      icon="hub"
      title="No build provider yet"
      description="Connect the Expo account your Expo apps build on (its name as on expo.dev, a robot token, your plan's slots), or a Bitrise workspace for native apps."
    />
  );
}

/** The Bitbucket workspaces repositories come from. */
function SourceControlSection({ connections, scm }: { connections: ReturnType<typeof useScmConnections>; scm: ScmActions }) {
  const view = listView(connections, connections.data);
  return (
    <div data-testid="gs-source-control" className="mt-3 flex flex-col gap-2">
      <h3 className="text-[13px] font-semibold text-foreground">Source control</h3>
      {view === 'loading' ? <RowsSkeleton rows={1} /> : null}
      {view === 'error' ? <ErrorState compact error={connections.error} onRetry={() => void connections.refetch()} title="Couldn't load the source control connections" /> : null}
      {view === 'empty' ? (
        <EmptyState
          compact
          testId="gs-scm-empty"
          icon="account_tree"
          title="No Bitbucket workspace yet"
          description="Connect your workspace with an access token (Repositories read and admin): Connect app then offers its repositories, and Hangar reads your source from it."
        />
      ) : null}
      {view === 'list' ? <ScmConnectionsList connections={connections.data!} actions={scm} /> : null}
    </div>
  );
}

/**
 * Step 1 · Build service: the provider accounts builds run on and the Bitbucket workspace repositories come from.
 * Malible provisions the provider accounts (SPEC decision 19): client admins read that the build service is ready and
 * only connect Bitbucket, never the providers behind it (SPEC decision 23); platform operators see the accounts and
 * connect, edit, re-token, test and disable them.
 */
export function ProvidersStep({ setup, index }: StepProps) {
  const actions = useProviderAccountActions();
  const featured = useFeature('sourceControl') !== false;
  const connections = useScmConnections(featured);
  const sourceControl = featured && !scmUnsupported(connections.error);
  const scm = useScmConnectionActions();
  const current = setup.progress.next === 'provider';
  return (
    <>
      <StepCard
        state={setup.state('provider')}
        current={current}
        index={index}
        title={STEP_TITLE.provider}
        summary={actions.operator ? 'Your builds run on Expo EAS (Expo apps) and Bitrise (native apps). Connect Bitbucket so Hangar can read your repositories.' : "Your builds run on Hangar's build service, which your Hangar administrators set up for you. Connect Bitbucket so Hangar can read your repositories."}
        collapsedNote={actions.operator ? providersNote(setup.providerAccounts) : clientProvidersNote(setup.providerAccounts.filter((a) => a.status !== 'disabled').length)}
        footnote={<ProvidersFootnote bitrise={setup.bitrise} operator={actions.operator} />}
        footer={<ProvidersFooter actions={actions} scm={scm} sourceControl={sourceControl} bitrise={setup.bitrise} current={current} />}
      >
        <AccountsBody setup={setup} actions={actions} />
        {sourceControl ? <SourceControlSection connections={connections} scm={scm} /> : null}
      </StepCard>
      {actions.overlays}
      {scm.overlays}
    </>
  );
}
